import { call, select } from 'typed-redux-saga';
import { selectRobotCleaningControlPraDeviceId } from '../robotCleaningControlSelectors';
import type { RobotCleaningControlService } from 'app/modules/machine-inventory/RobotCleaningControlService';

/**
 * The position subscription needs the PRA device id, which differs from the machine id the rest of
 * the app uses (the machine id gives "Device not found"). Looked up by material and serial number.
 * Null when it can't be resolved, so the caller doesn't subscribe with a wrong id.
 */
export const resolvePraDeviceId = async (
  robotCleaningControlService: RobotCleaningControlService,
  materialNumber: string,
  serialNumber: string
): Promise<string | null> => {
  try {
    const data = await robotCleaningControlService.getDevicePausedState({ materialNumber, serialNumber });

    return data?.device?.id ?? null;
  } catch (error) {
    console.error(error);

    return null;
  }
};

/**
 * The PRA device id: the one the paused-state query already reported, so opening the live map or sending a
 * command doesn't repeat that query. Looked up only when it hasn't reported yet (or failed). Null when it
 * can't be resolved.
 *
 * @yields the stored id, or the device lookup when none is stored
 */
// No explicit Generator<...> return type: call() needs it inferred to type the result (see fetchLiveMapData).
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* getPraDeviceId(
  robotCleaningControlService: RobotCleaningControlService,
  materialNumber: string,
  serialNumber: string
) {
  const storedPraDeviceId = yield* select(selectRobotCleaningControlPraDeviceId);

  if (storedPraDeviceId) {
    return storedPraDeviceId;
  }

  return yield* call(resolvePraDeviceId, robotCleaningControlService, materialNumber, serialNumber);
}
